import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth, startGoogleLogin } from "@/lib/auth";
import { useLocale } from "@/lib/locale";
import { fetchThreads } from "@/lib/api";
import { Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Messages() {
  const { user, loading: authLoading } = useAuth() || {};
  const { t } = useLocale();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading || !user) return;
    fetchThreads().then(setThreads).finally(() => setLoading(false));
  }, [authLoading, user]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber" /></div>;

  if (!user) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <Mail className="h-10 w-10 text-amber mx-auto mb-4" />
          <h1 className="font-display text-4xl text-white">{t("inbox")}</h1>
          <Button onClick={startGoogleLogin} className="mt-8 bg-amber hover:bg-amber-hover text-obsidian font-heading">
            {t("sign_in")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-14" data-testid="messages-page">
        <div className="text-[11px] uppercase tracking-[0.25em] text-amber font-heading mb-2">DM</div>
        <h1 className="font-display text-4xl sm:text-5xl text-white mb-8">{t("inbox")}</h1>

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-amber" /></div>
        ) : threads.length === 0 ? (
          <div className="border border-white/10 rounded-md p-10 text-center bg-obsidian-surface/60" data-testid="messages-empty">
            <p className="font-display text-2xl text-white">{t("no_messages")}</p>
            <p className="text-zinc-500 font-body mt-2">Start a chat from any fan board post.</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="threads-list">
            {threads.map((th) => (
              <Link
                key={th.user_id}
                to={`/messages/${th.user_id}`}
                className="flex items-center gap-4 border border-white/10 rounded-md p-4 bg-obsidian-surface/60 hover:border-amber/40 transition"
                data-testid={`thread-${th.user_id}`}
              >
                {th.picture ? (
                  <img src={th.picture} alt={th.name} className="h-12 w-12 rounded-full object-cover border border-white/10" />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-amber/20 text-amber font-heading flex items-center justify-center">{th.name?.[0]}</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-heading text-white">{th.name}</div>
                    {th.unread > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full bg-amber text-obsidian text-[10px] font-heading">{th.unread}</span>
                    )}
                  </div>
                  <div className="text-sm text-zinc-500 truncate font-body">{th.last_message}</div>
                </div>
                <div className="text-xs text-zinc-600 font-heading">{new Date(th.last_at).toLocaleDateString()}</div>
              </Link>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
